import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Eye, Swords, Trophy, Crown, Zap, Flame } from "lucide-react";
import BullpenArena from "@/components/BullpenArena";

const EMOJI = { fire: "🔥", clap: "👏", shock: "😱", zap: "⚡" };
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function CrowdStrip({ crowd, vip }) {
  const others = crowd.filter((c) => !c.is_me);
  const ring = vip ? "border-yellow-400/80" : "border-teal-400/80";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="sb-crowd">
      <div className="flex -space-x-2.5">
        {crowd.slice(0, 10).map((c, i) => (
          <span key={c.id} data-testid={`sb-seat-${c.id}`} style={{ animationDelay: `${i * 90}ms`, zIndex: 20 - i }}
            title={`${c.name}${c.is_me ? " (you)" : ""}${c.watching ? " · watching a clash" : " · in the lounge"}`}
            className={`sb-seat relative w-8 h-8 rounded-full border-2 ${c.is_me ? "border-white" : ring} bg-black overflow-hidden flex items-center justify-center shrink-0`}>
            {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" />
              : <span className={`text-[9px] font-black ${vip ? "text-yellow-300" : "text-teal-300"}`}>{initials(c.name)}</span>}
            {c.watching && <span className="absolute -bottom-0.5 -right-0.5 text-[8px]">👁</span>}
          </span>
        ))}
      </div>
      <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-400" data-testid="sb-crowd-count">
        {others.length === 0 ? "Just you in the suite — front row is yours"
          : `You + ${others.length} other${others.length === 1 ? "" : "s"} in the suite`}
      </span>
    </div>
  );
}


const SB_CSS = `
@keyframes sbBurst{0%{transform:translateY(0) scale(.6);opacity:0}15%{opacity:1}100%{transform:translateY(-140px) scale(1.5);opacity:0}}
@keyframes sbTicker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes sbGlow{0%,100%{box-shadow:0 0 12px rgba(45,212,191,.25),inset 0 0 24px rgba(45,212,191,.06)}50%{box-shadow:0 0 26px rgba(45,212,191,.5),inset 0 0 24px rgba(45,212,191,.1)}}
@keyframes sbSeatIn{0%{transform:translateY(10px) scale(.6);opacity:0}100%{transform:translateY(0) scale(1);opacity:1}}
@keyframes sbSeatPulse{0%,100%{box-shadow:0 0 6px rgba(45,212,191,.4)}50%{box-shadow:0 0 14px rgba(45,212,191,.8)}}
.sb-seat{animation:sbSeatIn .5s ease-out both,sbSeatPulse 3s ease-in-out infinite}
.sb-panel{background:rgba(3,10,12,.72);border:1px solid rgba(45,212,191,.35);backdrop-filter:blur(14px);animation:sbGlow 3.2s ease-in-out infinite}
.sb-panel-vip{background:rgba(14,9,2,.74);border:1px solid rgba(250,204,21,.45);backdrop-filter:blur(14px)}
.sb-burst{position:absolute;bottom:10px;pointer-events:none;font-size:26px;animation:sbBurst 1.4s ease-out forwards}
.sb-ticker-track{display:inline-flex;white-space:nowrap;animation:sbTicker 40s linear infinite}
`;

function SpectatorTheater({ replay, myCall, callResult, onReact, bursts, localReacts, onClose }) {
  const [shown, setShown] = useState(0);
  const totalR = replay.rounds.length;
  useEffect(() => {
    if (shown >= totalR + 1) return;
    const t = setTimeout(() => setShown((s) => s + 1), shown === 0 ? 600 : 1100);
    return () => clearTimeout(t);
  }, [shown, totalR]);
  const done = shown >= totalR + 1;
  const call = callResult || myCall;
  return (
    <div className="relative">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button data-testid="sb-theater-back" onClick={onClose} title="Back to the suite"
          className="text-[9px] font-black uppercase tracking-widest border border-teal-400/50 text-teal-300 px-2.5 py-1 hover:bg-teal-400 hover:text-black transition-colors">← Suite</button>
        <span className="text-[9px] font-black uppercase tracking-[0.3em] text-teal-300 border border-teal-400/50 bg-teal-400/10 px-2.5 py-1 animate-pulse">● LIVE FROM THE JUMBOTRON</span>
        {replay.format_label && <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 border border-zinc-600 px-2 py-1">{replay.format_label}</span>}
        <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-500"><Eye className="w-3 h-3" /> {replay.watchers} in the suite</span>
        {!done && <button data-testid="sb-skip-btn" onClick={() => setShown(totalR + 1)} className="ml-auto text-[9px] font-black uppercase tracking-widest border border-pb-border px-3 py-1 text-zinc-400 hover:text-white">Skip</button>}
      </div>
      {replay.rounds.slice(0, Math.min(shown, totalR)).map((rd) => (
        <div key={rd.round} className="border border-teal-400/20 bg-black/60 p-2.5 mb-2 animate-fade-up" data-testid={`sb-round-${rd.round}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] font-black uppercase tracking-[0.25em] text-zinc-500">{rd.dreambreaker ? "Overtime" : `Round ${rd.round}`}</span>
            {rd.dreambreaker && <span className="text-[9px] font-display font-black uppercase px-1.5 py-0.5 border border-pb-red text-pb-red bg-pb-red/10 animate-pulse">⚡ DREAMBREAKER</span>}
            <span className={`text-[9px] font-display font-black uppercase px-1.5 py-0.5 border ${rd.clutch ? "border-yellow-400 text-yellow-400 bg-yellow-400/10" : "border-teal-400/50 text-teal-300"}`}>{rd.stat}{rd.clutch ? " + CLUTCH" : ""}</span>
          </div>
          <div className="space-y-1">
            {[...rd.entries].sort((a, b) => b.points - a.points).map((e, i) => (
              <div key={i} className={`flex items-center gap-2 px-2 py-1 ${call?.pick === e.player ? "border border-teal-400/50 bg-teal-400/5" : "bg-white/[.03]"}`}>
                {e.card_url ? <img src={e.card_url} alt="" className="w-5 h-7 object-cover shrink-0" /> : <span className="w-5 h-7 bg-black shrink-0" />}
                <span className="flex-1 min-w-0 text-[11px] truncate text-zinc-300">
                  {e.player}{call?.pick === e.player ? <span className="text-teal-300 font-black"> (YOUR CALL)</span> : ""} <span className="text-zinc-600">·</span> <span className="text-zinc-500">{e.card_name}</span>
                </span>
                <span className="text-[11px] font-mono text-zinc-400 shrink-0">{e.value}{e.clutch_bonus ? <span className="text-yellow-400"> +{e.clutch_bonus}</span> : null}</span>
                <span className="shrink-0 w-7 text-center text-[11px] font-display font-black text-zinc-500">+{e.points}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {!done && <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 text-teal-300 animate-spin" /></div>}
      {done && (
        <div className="border border-teal-400/40 bg-teal-400/5 p-4 animate-fade-up" data-testid="sb-final">
          <div className="font-display font-black uppercase text-base mb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" /> {replay.winner_name} takes the court!
          </div>
          <div className="space-y-1 mb-3">
            {replay.standings.map((s, i) => (
              <div key={i} className={`flex items-center gap-3 px-3 py-1.5 ${s.name === replay.winner_name ? "border border-yellow-400/50 bg-yellow-400/10" : "bg-white/[.03]"}`}>
                <span className={`w-5 font-display font-black text-sm ${i === 0 ? "text-yellow-400" : "text-zinc-500"}`}>{i + 1}</span>
                <span className="flex-1 text-xs font-semibold">{s.name}{call?.pick === s.name ? <span className="text-teal-300"> ◈ your call</span> : ""}</span>
                <span className="font-mono font-bold text-xs">{s.points} pts</span>
              </div>
            ))}
          </div>
          {call && (
            <div data-testid="sb-call-result" className={`text-center px-3 py-2.5 border font-black uppercase tracking-widest text-xs mb-2 ${call.correct ? "border-[#A3E635]/60 bg-[#A3E635]/10 text-[#A3E635]" : "border-pb-red/50 bg-pb-red/5 text-pb-red"}`}>
              {call.correct ? `🎯 YOU CALLED IT!${callResult ? ` Streak: ${callResult.streak}` : ""}` : "❌ Missed the call — streak reset"}
              {callResult?.bonus > 0 && <div className="text-yellow-400 mt-1">+{callResult.bonus} BLC sharpshooter bonus 🏆</div>}
            </div>
          )}
          <button data-testid="sb-back-btn" onClick={onClose}
            className="w-full border border-teal-400/60 text-teal-300 font-black uppercase tracking-widest py-2.5 text-xs hover:bg-teal-400 hover:text-black transition-colors">
            Back to the Suite
          </button>
        </div>
      )}
      {/* Reactions bar */}
      <div className="sticky bottom-0 mt-3 flex items-center justify-center gap-3 py-2" data-testid="sb-reactions-bar">
        {Object.entries(EMOJI).map(([k, e]) => (
          <button key={k} data-testid={`sb-react-${k}`} onClick={() => onReact(k)}
            className="relative w-11 h-11 border border-teal-400/40 bg-black/80 text-xl hover:border-teal-300 hover:scale-110 active:scale-95 transition-transform">
            {e}
          </button>
        ))}
        <span className="text-[9px] font-mono uppercase tracking-widest text-teal-300/80 ml-1" data-testid="sb-hype-count">
          🔊 {Object.values(replay.reactions || {}).reduce((a, b) => a + b, 0) + localReacts} hype
        </span>
        {bursts.map((b) => (
          <span key={b.id} className="sb-burst" style={{ left: `${b.x}%` }}>{EMOJI[b.emoji]}</span>
        ))}
      </div>
    </div>
  );
}

export default function Skybox({ meId, onClose }) {
  const [data, setData] = useState(null);
  const [lb, setLb] = useState([]);
  const [callPrompt, setCallPrompt] = useState(null);
  const [theater, setTheater] = useState(null);
  const [callResult, setCallResult] = useState(null);
  const [showArena, setShowArena] = useState(false);
  const [bursts, setBursts] = useState([]);
  const [localReacts, setLocalReacts] = useState(0);
  const [crowd, setCrowd] = useState([]);
  const watchingRef = useRef(null);
  const burstId = useRef(0);
  const load = () => {
    api.get("/cardwars/skybox/feed").then(({ data: d }) => setData(d)).catch(() => setData({ battles: [], ticker: [] }));
    api.get("/cardwars/skybox/leaderboard").then(({ data: d }) => setLb(d)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const ping = () => api.post("/cardwars/skybox/presence", { watching: watchingRef.current })
      .then(({ data: d }) => setCrowd(d.crowd)).catch(() => {});
    ping();
    const iv = setInterval(ping, 20000);
    return () => { clearInterval(iv); api.delete("/cardwars/skybox/presence").catch(() => {}); };
  }, []);
  useEffect(() => {
    watchingRef.current = theater?.battle_id || null;
    api.post("/cardwars/skybox/presence", { watching: watchingRef.current })
      .then(({ data: d }) => setCrowd(d.crowd)).catch(() => {});
  }, [theater?.battle_id]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const openReplay = async (bid) => {
    try {
      const { data: r } = await api.get(`/cardwars/skybox/battles/${bid}`);
      setLocalReacts(0);
      setTheater(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openBattle = (b) => {
    setCallResult(null);
    if (!b.watched && !b.is_mine && !b.my_call) setCallPrompt(b);
    else openReplay(b.id);
  };
  const exitTheater = () => { setTheater(null); setCallPrompt(null); setCallResult(null); setLocalReacts(0); load(); };
  const makeCall = async (b, pick) => {
    setCallPrompt(null);
    if (pick) {
      try {
        const { data: r } = await api.post(`/cardwars/skybox/battles/${b.id}/call`, { winner_name: pick });
        setCallResult({ ...r, pick });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    openReplay(b.id);
  };
  const react = (emoji) => {
    if (!theater) return;
    const id = ++burstId.current;
    setLocalReacts((n) => n + 1);
    setBursts((s) => [...s, { id, emoji, x: 30 + Math.random() * 40 }]);
    setTimeout(() => setBursts((s) => s.filter((x) => x.id !== id)), 1500);
    api.post(`/cardwars/skybox/battles/${theater.battle_id}/react`, { emoji }).catch(() => {});
  };

  const vip = !!data?.event?.vip;
  const panelCls = vip ? "sb-panel-vip" : "sb-panel";

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto overflow-x-hidden overscroll-contain" data-testid="skybox-overlay">
      <style>{SB_CSS}</style>
      <div className="fixed inset-0 z-0">
        <img src={vip ? "/sdb/skybox-vip.jpg" : "/sdb/skybox.jpg"} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/85" />
      </div>
      <div className="relative z-[1] max-w-6xl mx-auto p-4 sm:p-6 pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className={`font-display font-black uppercase text-2xl flex items-center gap-2 ${vip ? "text-yellow-400" : "text-teal-300"}`}
            style={{ textShadow: vip ? "0 0 18px rgba(250,204,21,.6)" : "0 0 18px rgba(45,212,191,.6)" }}>
            🏟 The Skybox {vip && <span className="text-xs border border-yellow-400 px-2 py-1 tracking-[0.3em] animate-pulse">FRIDAY VIP SUITE</span>}
          </div>
          <span className="text-[9px] uppercase tracking-[0.3em] text-zinc-400">Watch the Bullpen from above</span>
          <button data-testid="skybox-close" onClick={() => (theater || callPrompt) ? exitTheater() : onClose()}
            title={(theater || callPrompt) ? "Back to the suite" : "Leave the Skybox"}
            className="ml-auto border border-zinc-600 p-2 text-zinc-400 hover:text-white hover:border-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-5 -mt-2"><CrowdStrip crowd={crowd} vip={vip} /></div>

        {!data ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-teal-300 animate-spin" /></div>
        ) : (
          <>
            {/* Friday VIP banner */}
            {data.event && (
              <div className={`${panelCls} p-4 mb-4 flex flex-wrap items-center gap-4`} data-testid="sb-event-banner">
                <span className={`font-display font-black uppercase flex items-center gap-2 ${vip ? "text-yellow-400" : "text-pb-red"}`}>
                  <Flame className="w-4 h-4" /> Friday Night Bullpen Battle
                </span>
                <span className="text-xs text-zinc-300">Live pot <b className="text-yellow-400 font-mono">{data.event.pot} BLC</b> · <b className="text-white">{data.event.entrants}</b> in the pool</span>
                <button data-testid="sb-arena-btn" onClick={() => setShowArena(true)}
                  className="ml-auto text-[10px] font-black uppercase tracking-widest border border-yellow-400/70 text-yellow-400 px-4 py-2 hover:bg-yellow-400 hover:text-black transition-colors">
                  🏆 Watch the Bracket Show
                </button>
              </div>
            )}

            <div className="grid lg:grid-cols-[1fr_340px] gap-4">
              {/* Jumbotron */}
              <div className={`${panelCls} p-3 sm:p-4 min-w-0 overflow-hidden ${(theater || callPrompt) ? "order-1 min-h-[220px]" : "order-2 min-h-[150px]"} lg:order-1 lg:min-h-[340px]`} data-testid="sb-jumbotron">
                <div className="text-[9px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-3">— The Jumbotron —</div>
                {theater ? (
                  <SpectatorTheater replay={theater} myCall={theater.my_call} callResult={callResult}
                    onReact={react} bursts={bursts} localReacts={localReacts}
                    onClose={exitTheater} />
                ) : callPrompt ? (
                  <div className="text-center py-8 animate-fade-up" data-testid="sb-call-prompt">
                    <div className="font-display font-black uppercase text-lg text-teal-300 mb-1">🎯 Call It</div>
                    <p className="text-xs text-zinc-400 mb-1">Who takes this {callPrompt.format_label} clash? Nail 5 in a row for a <b className="text-yellow-400">+{data.streak_bonus_blc} BLC</b> sharpshooter bonus.</p>
                    <p className="text-[9px] uppercase tracking-widest text-zinc-600 mb-4">{callPrompt.challenger} challenged the court</p>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      {callPrompt.players.map((p) => (
                        <button key={p} data-testid={`sb-call-${p.replace(/\s+/g, "-")}`} onClick={() => makeCall(callPrompt, p)}
                          className="border border-teal-400/60 text-teal-200 text-xs font-black uppercase tracking-widest px-4 py-2.5 hover:bg-teal-400 hover:text-black transition-colors">
                          <Swords className="w-3 h-3 inline mr-1.5" />{p}
                        </button>
                      ))}
                    </div>
                    <button data-testid="sb-just-watch" onClick={() => makeCall(callPrompt, null)}
                      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white underline underline-offset-4">
                      Just watch — no call
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8 lg:py-16 text-zinc-500" data-testid="sb-jumbotron-idle">
                    <Eye className="w-8 h-8 mx-auto mb-3 text-teal-400/50" />
                    <div className="text-xs uppercase tracking-[0.3em]">Pick a battle from the rail<br /><span className="text-[9px] text-zinc-600">it plays here on the big screen</span></div>
                  </div>
                )}
              </div>

              {/* Feed rail + leaderboard */}
              <div className={`space-y-4 min-w-0 ${(theater || callPrompt) ? "order-2" : "order-1"} lg:order-2`}>
                <div className={`${panelCls} p-4`} data-testid="sb-feed">
                  <div className="text-[9px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-3">On the Court this week</div>
                  {data.battles.length === 0 ? (
                    <div className="text-xs text-zinc-500 py-4 text-center">No ranked clashes yet — the court is quiet…</div>
                  ) : (
                    <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                      {data.battles.map((b) => {
                        const liveEyes = crowd.filter((c) => !c.is_me && c.watching === b.id).length;
                        return (
                        <button key={b.id} data-testid={`sb-battle-${b.id}`} onClick={() => openBattle(b)}
                          className="w-full text-left px-3 py-2 bg-black/50 border border-transparent hover:border-teal-400/50 transition-colors group">
                          <div className="flex items-center gap-2">
                            <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-teal-500/50 text-teal-300 shrink-0">{b.format_label}</span>
                            <span className="flex-1 min-w-0 text-[11px] text-zinc-300 truncate"><b className="text-white">{b.challenger}</b> vs {b.players.filter((p) => p !== b.challenger).slice(0, 2).join(", ")}{b.players.length > 3 ? "…" : ""}</span>
                            {liveEyes > 0 && <span className="text-[8px] font-black text-teal-300 border border-teal-400/50 bg-teal-400/10 px-1 py-0.5 shrink-0 animate-pulse" data-testid={`sb-live-eyes-${b.id}`}>👁 {liveEyes} watching</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {b.winner ? <span className="text-[8px] uppercase tracking-widest text-yellow-400/90">🏆 {b.winner}</span>
                              : <span className="text-[8px] uppercase tracking-widest text-teal-400/80 group-hover:text-teal-300">🎯 tap to call it & watch</span>}
                            {b.my_call && <span className={`text-[8px] font-black ${b.my_call.correct ? "text-[#A3E635]" : "text-pb-red"}`}>{b.my_call.correct ? "✓ called it" : "✗ missed"}</span>}
                            {b.reactions > 0 && <span className="text-[8px] text-zinc-500 ml-auto">🔥 {b.reactions}</span>}
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className={`${panelCls} p-4`} data-testid="sb-leaderboard">
                  <div className="text-[9px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-1">Sharpest Eyes in the Suite</div>
                  <div className="text-[8px] uppercase tracking-widest text-zinc-600 mb-2.5">Your record: {data.me.hits}/{data.me.calls} calls · streak {data.me.streak} · best {data.me.best_streak}</div>
                  {lb.length === 0 ? (
                    <div className="text-[10px] text-zinc-600 py-2">Nobody's called a match yet — be the first.</div>
                  ) : lb.map((r, i) => (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1.5 text-[11px] ${r.is_me ? "border border-teal-400/40 bg-teal-400/5" : ""}`}>
                      <span className={`w-4 font-display font-black ${i === 0 ? "text-yellow-400" : "text-zinc-500"}`}>{i + 1}</span>
                      {i === 0 && <Crown className="w-3 h-3 text-yellow-400" />}
                      <span className="flex-1 truncate">{r.name}{r.is_me ? " (you)" : ""}</span>
                      <span className="font-mono text-zinc-400">{r.hits}/{r.calls}</span>
                      <span className="font-mono text-teal-300" title="best streak"><Zap className="w-2.5 h-2.5 inline" />{r.best_streak}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Ticker */}
      {data?.ticker?.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-[2] bg-black/85 border-t border-teal-400/30 py-2 overflow-hidden" data-testid="sb-ticker">
          <div className="sb-ticker-track">
            {[...data.ticker, ...data.ticker].map((t, i) => (
              <span key={i} className="text-[10px] uppercase tracking-widest text-teal-200/90 px-8">◆ {t.content?.slice(0, 140)}</span>
            ))}
          </div>
        </div>
      )}

      {showArena && <BullpenArena meId={meId} onClose={() => setShowArena(false)} />}
    </div>
  );
}
